import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "./api";

type BillingRow = {
  id: number;
  name: string;
  trialEndsAt: string | null;
  subscriptionStatus: string;
  currentPeriodEnd: string | null;
  billingInterval: string | null;
  cancelAtPeriodEnd: boolean;
  state: "trialing" | "trial_expired" | "active" | "past_due" | "canceled" | "none";
  daysRemaining: number | null;
};

const STATE_STYLES: Record<string, string> = {
  active: "bg-teal-100 text-teal-700",
  trialing: "bg-blue-100 text-blue-700",
  trial_expired: "bg-red-100 text-red-700",
  past_due: "bg-amber-100 text-amber-700",
  canceled: "bg-gray-200 text-gray-700",
  none: "bg-gray-100 text-gray-500",
};

const Billing: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<BillingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [billingInterval, setBillingInterval] = useState<"month" | "year">("month");
  const [params] = useSearchParams();
  const checkout = params.get("checkout");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/billing/status");
      if (!res.ok) { setError(t("billing.failedToLoad")); return; }
      setRows(await res.json());
    } catch {
      setError(t("billing.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Stripe sends the browser back here after Checkout. That return is NOT
  // proof of payment, only the webhook is, so this just re-reads our own
  // state, which the webhook may or may not have updated yet.
  useEffect(() => {
    if (checkout !== "success") return;
    const timers = [1200, 3000, 6000].map((ms) => window.setTimeout(load, ms));
    return () => timers.forEach(window.clearTimeout);
  }, [checkout, load]);

  const subscribe = async (restaurantId: number) => {
    setBusyId(restaurantId);
    setError(null);
    try {
      const res = await apiFetch("/api/billing/checkout", {
        method: "POST",
        body: JSON.stringify({ restaurantId, interval: billingInterval }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) { setError(data.error || t("billing.failedToStart")); return; }
      window.location.href = data.url;
    } catch {
      setError(t("billing.failedToStart"));
    } finally {
      setBusyId(null);
    }
  };

  const openPortal = async () => {
    setError(null);
    try {
      const res = await apiFetch("/api/billing/portal", { method: "POST" });
      const data = await res.json();
      if (!res.ok || !data.url) { setError(data.error || t("billing.failedPortal")); return; }
      window.location.href = data.url;
    } catch {
      setError(t("billing.failedPortal"));
    }
  };

  const hasAnyPaid = rows.some((r) => r.state === "active" || r.state === "past_due" || r.state === "canceled");

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <h1 className="text-2xl font-bold">{t("billing.title")}</h1>
      <p className="text-sm text-gray-500 mt-0.5 mb-6">{t("billing.subtitle")}</p>

      {checkout === "success" && (
        <div className="mb-4 text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded p-3">
          {t("billing.checkoutSuccess")}
        </div>
      )}
      {checkout === "cancelled" && (
        <div className="mb-4 text-sm text-gray-700 bg-gray-50 border rounded p-3">
          {t("billing.checkoutCancelled")}
        </div>
      )}
      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>}

      <div className="mb-5 inline-flex rounded-lg border overflow-hidden text-sm">
        <button
          onClick={() => setBillingInterval("month")}
          className={`px-4 py-2 font-medium ${billingInterval === "month" ? "bg-teal-600 text-white" : "bg-white text-gray-700"}`}
        >
          {t("billing.monthly")}
        </button>
        <button
          onClick={() => setBillingInterval("year")}
          className={`px-4 py-2 font-medium ${billingInterval === "year" ? "bg-teal-600 text-white" : "bg-white text-gray-700"}`}
        >
          {t("billing.annual")}
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">{t("common.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">{t("billing.noRestaurants")}</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="bg-white rounded-xl shadow p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="font-semibold text-gray-900">
                  {r.name}
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${STATE_STYLES[r.state]}`}>
                    {t(`billing.state.${r.state}`)}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {r.state === "trialing" && r.daysRemaining !== null && (
                    <>{t("billing.trialDaysLeft", { count: r.daysRemaining })}</>
                  )}
                  {r.state === "trial_expired" && <>{t("billing.trialEnded")}</>}
                  {/* A cancelled subscription stays "active" in Stripe until the
                      paid period runs out, so this must not say "renews". */}
                  {r.state === "active" && r.currentPeriodEnd && (
                    <>{t(r.cancelAtPeriodEnd ? "billing.endsOn" : "billing.renewsOn",
                         { date: new Date(r.currentPeriodEnd).toLocaleDateString() })}</>
                  )}
                  {r.state === "past_due" && <>{t("billing.pastDue")}</>}
                </div>
              </div>
              <div className="shrink-0">
                {(r.state === "active" || r.state === "past_due") && !r.cancelAtPeriodEnd ? (
                  <button onClick={openPortal} className="px-4 py-2 border rounded-lg font-semibold text-sm text-gray-700 hover:bg-gray-50">
                    {t("billing.manage")}
                  </button>
                ) : r.cancelAtPeriodEnd ? (
                  <button onClick={openPortal} className="px-4 py-2 border border-amber-300 text-amber-700 rounded-lg font-semibold text-sm hover:bg-amber-50">
                    {t("billing.resume")}
                  </button>
                ) : (
                  <button
                    onClick={() => subscribe(r.id)}
                    disabled={busyId === r.id}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold text-sm hover:bg-teal-700 disabled:opacity-50"
                  >
                    {busyId === r.id ? t("billing.starting") : t("billing.subscribePrice", {
                      price: billingInterval === "year" ? "$499/yr" : "$49.99/mo",
                    })}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasAnyPaid && (
        <button onClick={openPortal} className="mt-5 text-sm text-teal-700 hover:underline">
          {t("billing.manageAll")}
        </button>
      )}
    </div>
  );
};

export default Billing;
